import { Button, Col, Dropdown, Form, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { FormEvent, useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useCurrentClient } from "../../../auth/api/getCurrentClient";
import SentimentIndicator from "../../../../components/SentimentIndicatorSelector";
import { InputDatePicker } from "../../../../components/InputDatePicker";
import { createAlertConfig, getAlertConfigById, updateAlertConfig } from "../api/alertValuesApi";
import { useNavigate, useParams } from "react-router-dom";
import { useAlert } from "../../../../hooks/useAlert";
import AlertMessage from "../../../../components/AlertMessage";
import { ApiErrorResponse } from "../../../../types/ApiErrorResponse";
import { COLOR } from "../../../../config/constraits";
import { AlertConfigFormDto } from "../types";
import { GenericSaveButton } from "../../../../components/common/GenericSaveButton";

export default function AlertConfigForm() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const isEditMode = !!id;
    const queryClient = useQueryClient();
    const { data: configClient } = useCurrentClient();
    const [selectedAlertValue, setSelectedAlertValue] = useState<number | null>(null);
    const [selectedGeneratesAlert, setSelectedGeneratesAlert] = useState<boolean | null>(null);
    const [startDate, setStartDate] = useState<Date | null>(null);
    const [endDate, setEndDate] = useState<Date | null>(null);
    const [criteria, setCriteria] = useState<string>("");
    const [examples, setExamples] = useState<string[]>([""]);
    const [loading, setLoading] = useState(false);
    const [loadingData, setLoadingData] = useState(isEditMode);
    const [errors, setErrors] = useState<{
        alertValue?: string;
        generatesAlert?: string;
        startDate?: string;
        endDate?: string;
        criteria?: string;
    }>({});
    const { alert, hideAlert, showAlert, isVisible } = useAlert({ fromLocation: false });

    const generatesAlertOptions = [
        { value: true, label: t("claim:withAlert"), iconClassName: "bi bi-exclamation-triangle-fill", color: COLOR.RED },
        { value: false, label: t("claim:withoutAlert"), iconClassName: "bi bi-exclamation-circle", color: COLOR.TARONJA }
    ];

    const selectedOption = generatesAlertOptions.find((o) => o.value === selectedGeneratesAlert);

    useEffect(() => {
        if (!isEditMode) return;

        const loadAlertConfig = async () => {
            setLoadingData(true);
            try {
                const data = await getAlertConfigById(Number(id));
                setSelectedAlertValue(data.value);
                setSelectedGeneratesAlert(data.generatesAlert);
                setStartDate(data.startDate ? new Date(data.startDate) : null);
                setEndDate(data.endDate ? new Date(data.endDate) : null);
                setCriteria(data.criteria ?? "");
                setExamples(data.examples?.length ? data.examples : [""]);
            } catch (error) {
                const fetchError = error as ApiErrorResponse;
                showAlert({
                    message: fetchError?.message ?? t("common:requestError.errorBadRequest"),
                    type: "danger",
                    duration: 8000
                });
            } finally {
                setLoadingData(false);
            }
        };

        loadAlertConfig();
    }, [id, isEditMode, showAlert, t]);

    const handleExampleChange = (index: number, value: string) => {
        const nextExamples = [...examples];
        nextExamples[index] = value;
        setExamples(nextExamples);
    };

    const handleAddExample = () => {
        setExamples((previous) => [...previous, ""]);
    };

    const handleRemoveExample = (indexToRemove: number) => {
        setExamples((previous) => {
            if (previous.length <= 1) {
                return previous;
            }
            return previous.filter((_, index) => index !== indexToRemove);
        });
    };

    const validateForm = () => {
        const newErrors: {
            alertValue?: string;
            generatesAlert?: string;
            startDate?: string;
            endDate?: string;
            criteria?: string;
        } = {};

        if (selectedAlertValue === null) {
            newErrors.alertValue = t("common:form.requiredFieldForm");
        } else if (!Number.isInteger(selectedAlertValue)) {
            newErrors.alertValue = t("common:form.requiredFieldForm");
        }
        if (selectedGeneratesAlert === null) {
            newErrors.generatesAlert = t("common:form.requiredFieldForm");
        }
        // TODO: descomentar cuando entre fecha vigencia
        // if (!startDate) {
        //     newErrors.startDate = t("common:form.requiredFieldForm");
        // }
        // if (startDate && endDate && startDate > endDate) {
        //     newErrors.endDate = t("common:form.messageErrorFinishDateBeforeStartDate");
        // }
        if (!criteria.trim()) {
            newErrors.criteria = t("common:form.requiredFieldForm");
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e?: FormEvent) => {
        e?.preventDefault();
        if (!validateForm() || loading || selectedAlertValue === null || selectedGeneratesAlert === null) {
            return;
        }

        const payload: AlertConfigFormDto = {
            value: selectedAlertValue,
            generatesAlert: selectedGeneratesAlert,
            validityStartDate: startDate ?? new Date(),
            validityEndDate: endDate,
            criteria: criteria.trim(),
            examples: examples.map((item) => item.trim()).filter((item) => !!item)
        };

        setLoading(true);
        try {
            if (isEditMode) {
                await updateAlertConfig(Number(id), payload);
            } else {
                await createAlertConfig(payload);
            }

            queryClient.invalidateQueries({ queryKey: ['alertConfigs'] });
            navigate("/admin/alertValues", {
                state: {
                    message: isEditMode
                        ? t("alert:form.updateSuccess", { defaultValue: "Alert value updated successfully" })
                        : t("alert:form.saveSuccess"),
                    type: "success"
                }
            });
        } catch (error) {
            const fetchError = error as ApiErrorResponse;
            showAlert({
                message: fetchError?.message ?? t("common:requestError.errorBadRequest"),
                type: "danger",
                duration: 8000
            });
        } finally {
            setLoading(false);
        }
    };

    const onGoBackClick = async () => {
        navigate("/admin/alertValues");
    }

    return (
        <>
            <Row className="mb-2">
                <Col xs={12} md={8}>
                    <h2>
                        {isEditMode
                            ? t("alert:form.editTitle", { defaultValue: "Edit alert value" })
                            : t("alert:form.title", { defaultValue: "Alert values parametrization" })}
                    </h2>
                </Col>
                <Col xs={12} md={4} className="text-end mt-2 mt-md-0">
                    <GenericSaveButton
                        onClick={() => handleSubmit()}
                        onCancelClick={onGoBackClick}
                        cancelLabel={isEditMode ? t("common:buttons.buttonCancel") : t("common:buttons.buttonBack")}
                        saveLabel={loading ? t("common:buttons.buttonSaveingProgress") : t("common:buttons.buttonSave")}
                        disabled={loading || loadingData}
                    />
                </Col>
                <Col xs={12}>
                    {isVisible && (
                        <AlertMessage
                            message={alert!.message}
                            type={alert!.type}
                            onClose={hideAlert}
                        />
                    )}
                </Col>
            </Row>

            <Row>
                <Col>
                    <Form onSubmit={handleSubmit}>
                        <Row>
                            <Col xs={4} md={6}>
                                <Row style={{ marginTop: '20px' }}>
                                    <Col xs={6} md={12}>
                                        <SentimentIndicator
                                            value={selectedAlertValue}
                                            onChange={setSelectedAlertValue}
                                            label={t("alert:alertValue", { defaultValue: "Alert value" })}
                                            showSentimentLabel={false}
                                            showColorDot={false}
                                            minValue={configClient?.alertMinimumValue}
                                            maxValue={configClient?.alertMaximumValue}
                                            forceRangeOptions={true}
                                            disabled={loadingData || isEditMode}
                                        />
                                        {errors.alertValue && (
                                            <div className="invalid-feedback d-block">{errors.alertValue}</div>
                                        )}
                                    </Col>
                                </Row>
                            </Col>
                            <Col xs={6} md={6}>
                                <Row style={{ marginTop: '20px' }}>
                                    <Col xs={4} md={12}>
                                        <Form.Label>{t("alert:alertType", { defaultValue: "Generate alert" })}</Form.Label>
                                        <Dropdown>
                                            <Dropdown.Toggle
                                                variant="light"
                                                className="form-select dropdown-no-caret dropdown-select d-flex align-items-center justify-content-between text-start"
                                                disabled={loadingData || isEditMode}
                                            >
                                                <span className="d-flex align-items-center gap-2">
                                                    {selectedOption ? (
                                                        <i className={selectedOption.iconClassName} style={{ color: selectedOption.color, fontSize: "16px" }} />
                                                    ) : (
                                                        <span style={{ width: "16px", height: "16px", display: "inline-block", borderRadius: "4px", border: "1px solid #ccc" }} />
                                                    )}
                                                    {selectedOption?.label ?? t("common:selectDefaultText")}
                                                </span>
                                            </Dropdown.Toggle>
                                            <Dropdown.Menu className="w-100 dropdown-menu-text-black">
                                                <Dropdown.Item onClick={() => setSelectedGeneratesAlert(null)}>
                                                    {t("common:selectDefaultText")}
                                                </Dropdown.Item>
                                                {generatesAlertOptions.map((option) => (
                                                    <Dropdown.Item
                                                        key={String(option.value)}
                                                        onClick={() => setSelectedGeneratesAlert(option.value)}
                                                        className="d-flex align-items-center gap-2"
                                                    >
                                                        <i className={option.iconClassName} style={{ color: option.color, fontSize: "16px" }} />
                                                        {option.label}
                                                    </Dropdown.Item>
                                                ))}
                                            </Dropdown.Menu>
                                        </Dropdown>
                                        {errors.generatesAlert && (
                                            <div className="invalid-feedback d-block">{errors.generatesAlert}</div>
                                        )}
                                    </Col>
                                </Row>
                            </Col>
                        </Row>
                        {/* TODO: Descomentar cuando se utilicen las fechas de vigencia */}
                        <Row hidden>
                            <Col xs={4} md={6}>
                                <Row style={{ marginTop: '20px' }}>
                                    <Col xs={6} md={12}>
                                        <InputDatePicker
                                            label={t("alert:validityStartDate", { defaultValue: "Validity start date" })}
                                            value={startDate}
                                            onChange={(value) => setStartDate(value)}
                                            hasError={!!errors.startDate}
                                            errorMessage={errors.startDate}
                                            disabled={loadingData}
                                        />
                                    </Col>
                                </Row>
                            </Col>
                            <Col xs={6} md={6}>
                                <Row style={{ marginTop: '20px' }}>
                                    <Col xs={4} md={12}>
                                        <InputDatePicker
                                            label={t("alert:validityEndDate", { defaultValue: "Validity end date" })}
                                            value={endDate}
                                            onChange={(value) => setEndDate(value)}
                                            hasError={!!errors.endDate}
                                            errorMessage={errors.endDate}
                                            disabled={loadingData}
                                        />
                                    </Col>
                                </Row>
                            </Col>
                        </Row>

                        <Row style={{ marginTop: '20px' }}>
                            <Col xs={4} md={12}>
                                <Form.Label>{t("alert:form.criteria", { defaultValue: "Criteria" })}</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={criteria}
                                    onChange={(e) => setCriteria(e.target.value)}
                                    isInvalid={!!errors.criteria}
                                    disabled={loadingData}
                                />
                                <Form.Control.Feedback type="invalid">
                                    {errors.criteria}
                                </Form.Control.Feedback>
                            </Col>
                        </Row>

                        {examples.map((example, index) => (
                            <Row style={{ marginTop: '20px' }} key={`alert-example-${index}`}>
                                <Col xs={12} md={12}>
                                    <Form.Group>
                                        <Form.Label>{t("alert:form.example", { defaultValue: "Alert example" })}</Form.Label>
                                        <div className="d-flex gap-2 align-items-start">
                                            <Form.Control
                                                type="text"
                                                value={example}
                                                onChange={(e) => handleExampleChange(index, e.target.value)}
                                                disabled={loadingData}
                                            />
                                            <Button
                                                variant="light"
                                                type="button"
                                                onClick={() => handleRemoveExample(index)}
                                                disabled={examples.length === 1 || loadingData}
                                                className="rounded-circle d-flex align-items-center justify-content-center border-0 p-0"
                                                style={{ width: "32px", height: "32px", backgroundColor: "#e9ecef", color: "#6c757d", fontSize: "18px", lineHeight: 1 }}
                                                aria-label={t("common:buttons.buttonDelete")}
                                            >
                                                -
                                            </Button>
                                            {index === examples.length - 1 && (
                                                <Button
                                                    variant="light"
                                                    type="button"
                                                    onClick={handleAddExample}
                                                    disabled={loadingData}
                                                    className="rounded-circle d-flex align-items-center justify-content-center border-0 p-0"
                                                    style={{ width: "32px", height: "32px", backgroundColor: "#e9ecef", color: "#6c757d", fontSize: "18px", lineHeight: 1 }}
                                                    aria-label={t("alert:form.addExample", { defaultValue: "Add example" })}
                                                >
                                                    +
                                                </Button>
                                            )}
                                        </div>
                                    </Form.Group>
                                </Col>
                            </Row>
                        ))}
                    </Form>
                </Col>
            </Row>
        </>
    );
}
